<form [formGroup]="generalSettingsForm" (ngSubmit)="onSave()">
    <h5 class="card-title mb-4">General</h5>
    <div class="mb-3">
        <ms-input-label [labelText]="'Organization Name'" [isRequired]="true" />
        @if (isLoading()) {
            <div class="form-control placeholder-glow">
                <span class="placeholder col-7"></span>
            </div>
        } @else {
            <input type="text" id="name" class="form-control" formControlName="name"
                maxlength="100" required
                [class.is-invalid]="generalSettingsForm.get('name')?.invalid && generalSettingsForm.get('name')?.touched"
                [class.is-valid]="generalSettingsForm.get('name')?.valid && generalSettingsForm.get('name')?.touched && generalSettingsForm.get('name')?.value !== ''" />
            @if (generalSettingsForm.get('name')?.invalid && generalSettingsForm.get('name')?.touched) {
                <div class="error-messages">
                    @if (generalSettingsForm.get('name')?.errors?.['required']) {
                        <p class="text-danger text-start pt-1 mb-0">{{ FormValidatorMessages.OrganizationRequired }}</p>
                    }
                </div>
            }
        }
    </div>

    <ng-container *ifHasPermission="{ requiredPermissions: [OrganizationPermission.Update], userPermissionOnly: true }">
        <div class="mb-3">
            <ms-input-label [labelText]="'Organization Slug'" [isRequired]="true" />
            @if (isLoading()) {
                <div class="form-control placeholder-glow">
                    <span class="placeholder col-8"></span>
                </div>
            } @else {
                <input type="text" class="form-control" formControlName="slug"
                    [disableWithoutPermission]="{ requiredPermissions: [OrganizationPermission.Update], userPermissionOnly: true }"
                    [class.is-invalid]="generalSettingsForm.get('slug')?.invalid && generalSettingsForm.get('slug')?.touched"
                    [class.is-valid]="generalSettingsForm.get('slug')?.valid && generalSettingsForm.get('slug')?.touched && generalSettingsForm.get('slug')?.value !== ''"
                    placeholder="organization-slug"
                    maxlength="50" />
                @if (generalSettingsForm.get('slug')?.invalid && generalSettingsForm.get('slug')?.touched) {
                    <div class="error-messages">
                        @if (generalSettingsForm.get('slug')?.errors?.['required']) {
                            <p class="text-danger text-start pt-1 mb-0">{{ FormValidatorMessages.SlugRequired }}</p>
                        }
                        @if (generalSettingsForm.get('slug')?.errors?.['slug']) {
                            <p class="text-danger text-start pt-1 mb-0">{{ generalSettingsForm.get('slug')?.errors?.['slug']?.message }}</p>
                        }
                    </div>
                }
            }
        </div>
    </ng-container>

    <div class="mb-3">
        <ms-input-label [labelText]="'Reply-To Email Address'" [isRequired]="true" />
        @if (isLoading()) {
            <div class="form-control placeholder-glow">
                <span class="placeholder col-7"></span>
            </div>
        } @else {
            <input type="email" class="form-control" formControlName="replyToEmailAddress" required maxlength="255"
                [class.is-invalid]="generalSettingsForm.get('replyToEmailAddress')?.invalid && generalSettingsForm.get('replyToEmailAddress')?.touched"
                [class.is-valid]="generalSettingsForm.get('replyToEmailAddress')?.valid && generalSettingsForm.get('replyToEmailAddress')?.touched && generalSettingsForm.get('replyToEmailAddress')?.value !== ''"
                placeholder="noreply@example.com" />
            @if (generalSettingsForm.get('replyToEmailAddress')?.invalid && generalSettingsForm.get('replyToEmailAddress')?.touched) {
                <div class="error-messages">
                    @if (generalSettingsForm.get('replyToEmailAddress')?.errors?.['required']) {
                        <p class="text-danger text-start pt-1 mb-0">{{ FormValidatorMessages.OrganizationReplyToEmailAddressRequired }}</p>
                    }
                    @if (generalSettingsForm.get('replyToEmailAddress')?.errors?.['email']) {
                        <p class="text-danger text-start pt-1 mb-0">{{ generalSettingsForm.get('replyToEmailAddress')?.errors?.['email']?.message }}</p>
                    }
                </div>
            }
        }
    </div>

    <ng-container *ifHasPermission="{ requiredPermissions: [OrganizationPermission.Update], userPermissionOnly: true }">
        <h5 class="card-title mb-4 mt-5">Billing</h5>
        <div class="mb-3">
            <ms-input-label [labelText]="'Billing Status'" />
            @if (isLoading()) {
                <div class="form-control placeholder-glow">
                    <span class="placeholder col-6"></span>
                </div>
            } @else {
                <select class="form-select" formControlName="billingStatus">
                    @for (status of [BillingStatuses.Trial, BillingStatuses.Paid, BillingStatuses.Suspended, BillingStatuses.Demo]; track status) {
                        <option [value]="status">{{ status | formatLabel }}</option>
                    }
                </select>
            }
        </div>

        <div class="mb-3">
            <ms-input-label [labelText]="'Pricing Tier'" />
            @if (isLoading()) {
                <div class="form-control placeholder-glow">
                    <span class="placeholder col-5"></span>
                </div>
            } @else {
                <select class="form-select" formControlName="pricingTier">
                    @for (tier of [PricingTiers.Standard, PricingTiers.Premium, PricingTiers.WhiteGlove, PricingTiers.Custom, PricingTiers.Trial]; track tier) {
                        <option [value]="tier">{{ tier | formatLabel }}</option>
                    }
                </select>
            }
        </div>
    </ng-container>

    <h5 class="card-title mb-4 mt-5">Localization</h5>
    <div class="mb-3">
        <ms-input-label [labelText]="'Time Zone'" />
        @if (isLoading()) {
            <div class="form-control placeholder-glow">
                <span class="placeholder col-9"></span>
            </div>
        } @else {
            <ms-timezone-dropdown formControlName="timezone" />
        }
    </div>

    <div class="mb-3">
        <ms-input-label [labelText]="'Currency'" />
        @if (isLoading()) {
            <div class="form-control placeholder-glow">
                <span class="placeholder col-6"></span>
            </div>
        } @else {
            <ms-currency-dropdown formControlName="currency" />
        }
    </div>

    <h5 class="card-title mb-4 mt-5">Member Directory</h5>
    <div class="mb-3">
        @if (isLoading()) {
            <div class="placeholder-glow">
                <span class="placeholder col-6 d-block mb-2"></span>
                <span class="placeholder col-8 d-block"></span>
            </div>
        } @else {
            <div class="form-check form-switch fs-5">
                <input type="checkbox"
                    id="includeMembersInDirectoryByDefault"
                    class="form-check-input"
                    role="switch"
                    formControlName="includeMembersInDirectoryByDefault" />
                <label for="includeMembersInDirectoryByDefault" class="form-check-label fw-semibold fs-6">
                    {{ OrganizationsMessagingConstants.ORGANIZATIONS_DIRECTORY_DEFAULT_LABEL() }}
                </label>
            </div>
            <p class="text-muted small mb-0 mt-1">
                {{ OrganizationsMessagingConstants.ORGANIZATIONS_DIRECTORY_DEFAULT_HELP() }}
            </p>
        }
    </div>

    <div class="d-flex justify-content-end gap-2">
        <button type="submit" class="btn btn-primary" [disabled]="isLoading() || isSubmitting() || !hasFormBeenModified() || generalSettingsForm.invalid">
            @if (isSubmitting()) {
                <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Saving...
            } @else {
                Save
            }
        </button>
    </div>
</form>

<ms-info-modal #generalSettingsInfoModalRef
    modalId="generalSettingsInfoModal"
/>
